<script setup>
import { RouterView } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { setGlobalToast } from 'nemesiselements';
import { useRuntimeConfig } from '@/composables/useRuntimeConfig';
import ConfirmationEventBus from 'primevue/confirmationeventbus';
import ScrollbarFlutuante from '@/components/elementos/ScrollbarFlutuante/ScrollbarFlutuante.vue';
import ManutencaoView from '@/views/ManutencaoView.vue';

const toastRef = ref(null);

const authStore = useAuthStore();

const processandoAceiteConfirmacao = ref(false);

function aceitarConfirmacao(acceptCallback) {
  if (processandoAceiteConfirmacao.value) return;
  processandoAceiteConfirmacao.value = true;
  acceptCallback();
}

function liberarAceiteConfirmacao(options) {
  if (options?.group === 'confirmacao') {
    processandoAceiteConfirmacao.value = false;
  }
}

ConfirmationEventBus.on('confirm', liberarAceiteConfirmacao);

onBeforeUnmount(() => {
  ConfirmationEventBus.off('confirm', liberarAceiteConfirmacao);
});

window.addEventListener('storage', (event) => {
  if (event.key === 'logout-event') {
    authStore.logout();
    window.location.href = '/login';
  }
});

const { isDev: ambienteDev } = useRuntimeConfig();

onMounted(() => {
  setGlobalToast(toastRef.value);
});
</script>

<template>
  <ToastNotificacao ref="toastRef">
    <ConfirmDialog group="confirmacao">
      <template #container="{ message, acceptCallback, rejectCallback }">
        <div class="flex flex-column align-items-start p-5 surface-overlay border-round-3xl gap-1">
          <div :class="`w-3rem h-3rem border-round-xl flex align-items-center justify-content-center mb-3 
              ${message.severity === 'danger' ? 'bg-red-100' :
              message.severity === 'warning' ? 'bg-orange-100' :
                message.severity === 'success' ? 'bg-green-100' : ''}`">
            <i :class="`pi text-lg 
                ${message.severity === 'danger' ? 'pi-trash text-red-600' :
                message.severity === 'warning' ? 'pi-exclamation-triangle text-orange-700' :
                  message.severity === 'success' ? 'pi-check text-green-600' : ''}`"></i>
          </div>

          <p class="font-bold text-lg m-0 mb-1">{{ message.header }}</p>
          <p class="m-0 text-sm text-600 max-w-22rem" :class="{ 'pb-5': !message.message }">{{ message.subtitle }}</p>
          <p class="m-0 text-xs text-600 mt-4 flex flex-row align-itens-center gap-2 max-w-22rem"
            v-if="message.message">
            <span class="pi pi-info-circle text-xs"></span>{{ message.message }}
          </p>

          <div class="flex gap-3 justify-content-end w-full mt-4">
            <Button :label="message.rejectLabel" @click="rejectCallback()" text class="text-sm" severity="secondary"
              size="small"></Button>
            <Button @click="aceitarConfirmacao(acceptCallback)" :disabled="processandoAceiteConfirmacao"
              :label="message.acceptLabel" :severity="message.severity" class="text-sm" size="small"></Button>
          </div>
        </div>
      </template>
    </ConfirmDialog>

    <div v-if="ambienteDev" class="w-full bg-red-500 text-white text-xs pl-2" style="height: 2hv !important;">
      <i class="pi pi-exclamation-triangle text-xs py-2"></i> Você está no <span class="font-semibold">Ambiente de
        Desenvolvimento</span>.
    </div>

    <RouterView v-slot="{ Component, route }">
      <transition name="auth-fade" mode="out-in">
        <div :key="route.matched[0]?.path" class="auth-fade-wrapper">
          <component :is="Component" />
        </div>
      </transition>
    </RouterView>

    <ScrollbarFlutuante />
    <ManutencaoView />
  </ToastNotificacao>
</template>

<style scoped>
.auth-fade-wrapper {
  width: 100%;
}

.auth-fade-enter-active {
  transition: opacity 0.45s ease-out, transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 0.05s;
}

.auth-fade-leave-active {
  transition: opacity 0.25s ease-in, transform 0.25s ease-in;
}

.auth-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.auth-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

header {
  line-height: 1.5;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }
}
</style>